import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { chooseOnboardingLanguage } from '../../api/onboarding';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import CloudBand from '../../components/common/CloudBand';
import WanderWordsLogo from '../../components/common/WanderWordsLogo';
import OnboardingCard from '../../components/common/OnboardingCard';
import i18n from '../../i18n';
import s from './LanguagePage.module.css';

// Labels are LITERAL in their own language — never i18n keys. A user who can't
// read English must still recognise their own language here.
const LANGUAGES = [
  { key: 'en', label: 'English' },
  { key: 'zh', label: '中文' },
];

// First authenticated onboarding step (BP-ONBOARDING-LANGUAGE-STEP-01). Gated by
// the marker users.onboarding_language_chosen_at, orthogonal to onboarding_step.
// NOTE: must NOT call useOnboardingGuard — that hook is for steps 1–4 and would
// bounce this pre-step-0 page to child-profile.
export default function LanguagePage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Pre-select the card matching the language already in effect (e.g. ww-lang
  // detected from the marketing site).
  const [selected, setSelected] = useState(i18n.language === 'zh' ? 'zh' : 'en');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSelect(lang) {
    if (isSaving) return;
    const prev = i18n.language;
    setSelected(lang);
    setError(null);
    setIsSaving(true);

    // Flip the UI immediately, exactly like the Settings App Language toggle.
    i18n.changeLanguage(lang);

    try {
      // Sets app_language AND the onboarding marker atomically on the server.
      await chooseOnboardingLanguage(lang);
      // Refresh ['me'] so OnboardingGuard reads the now-set marker and
      // LanguageRestorer stays in sync with the persisted app_language.
      queryClient.invalidateQueries({ queryKey: ['me'] });
      // Explicit navigate avoids a guard-driven flash; the gate would also
      // route here→child-profile once the marker is set.
      navigate('/onboarding/child-profile', { replace: true });
    } catch (err) {
      // Revert the UI language like Settings does, surface a retry.
      i18n.changeLanguage(prev);
      setSelected(prev === 'zh' ? 'zh' : 'en');
      setError(err.message || t('common.genericError'));
      setIsSaving(false);
    }
  }

  return (
    <main className={s.page} ref={mainRef} tabIndex={-1}>
      {/* Teal hero block — CloudBand is decorative, absolutely pinned to its
          bottom (see CloudBand rule in CLAUDE.md/HANDOFF.md). It is never a
          layout element; the content below is laid out in normal flow. */}
      <div className={s.hero}>
        <WanderWordsLogo className={s.logo} />
        <div className={s.textBlock}>
          <h1 className={s.pageTitle}>{t('language.title')}</h1>
          <p className={s.subtitle}>{t('language.subtitle')}</p>
        </div>
        <CloudBand variant="cream" className={s.cloudBand} />
      </div>

      {/* Cream content block — selectable language cards */}
      <div className={s.content}>
        {error && (
          <div className={s.errorBanner} role="alert">{error}</div>
        )}
        <div className={s.cards} role="radiogroup" aria-label={t('language.title')}>
          {LANGUAGES.map((l) => (
            <OnboardingCard
              key={l.key}
              title={l.label}
              selected={selected === l.key}
              onClick={() => handleSelect(l.key)}
            />
          ))}
        </div>
      </div>
    </main>
  );
}
